import { WebhookDispatch } from "@/@types/WebhookDispatch";
import Loader from "@/components/Loader/Loader";
import BadgeShared from "@/components/shared/Badge/Badge";
import Divider from "@/components/shared/Divider/Divider";
import { usePermissions } from "@/auth/PermissionsContext";
import EditCustomerModal, {
  EDIT_CUSTOMER_MODAL,
} from "@/components/shared/EditCustomerModal/EditCustomerModal";
import ResendAccessConfirmationModal, {
  RESEND_ACCESS_CONFIRMATION_MODAL,
} from "@/components/shared/ResendAccessConfirmationModal/ResendAccessConfirmationModal";
import WebhookDetailsModal, {
  WEBHOOK_DETAILS_MODAL,
} from "@/components/shared/WebhookDetailsModal/WebhookDetailsModal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import LayoutInside from "@/layout/LayoutInside";
import { api } from "@/services/api";
import { EmailsService } from "@/services/emails.service";
import { IntegrationsService } from "@/services/integrations.service";
import { SellsService } from "@/services/sells.service";
import useModalStore from "@/store/useModalStore";
import { getIconMethodPayment } from "@/utils/getIconMethodPayment";
import { resolveSaleDisplayDate } from "@/utils/resolveSaleDisplayDate";
import { declineReasonForDisplay } from "@/utils/declineReasonForDisplay";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { countries } from "country-data-list";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Cog,
  CornerUpLeft,
  Edit,
  ExternalLink,
  History,
  Info,
  Send,
  XCircle,
} from "lucide-react";
import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Sticky, StickyContainer } from "react-sticky";
import ClientCircle from "@/assets/icons/ClientCircle";
import Conversion from "@/assets/icons/Conversion";
import Details from "@/assets/icons/Details";
import LinkCircle from "@/assets/icons/LinkCircle";
import RefundConversion from "@/assets/icons/RefundConversion";
import TagsCircle from "@/assets/icons/TagsCircle";
import WhatsIcon from "@/assets/icons/WhatsIcon";
import DeliveryTimeline from "@/components/sales/DeliveryTimeline";
import EmailsList from "@/components/sales/EmailsList";
import EmitNoteFiscalButton from "@/components/sales/EmitNoteFiscalButton";
import EmitReturnNoteFiscalButton from "@/components/sales/EmitReturnNoteFiscalButton";
import InvoiceStatusesSection from "@/components/sales/InvoiceStatusesSection";
import { RefundPendingModal } from "@/components/sales/RefundPendingModal";
import { RefundPayoutDetailsModal } from "@/components/sales/RefundPayoutDetailsModal";
import { RefundPixModal } from "@/components/sales/RefundPixModal";
import { RefundSuccessModal } from "@/components/sales/RefundSuccessModal";
import { RefusalHistoryModal } from "@/components/sales/RefusalHistoryModal";
import SalesCommissionsSection from "@/components/sales/SalesCommissionsSection";
import SaleSlider from "@/components/sales/SaleSlider";
import ShippingAddressSection from "@/components/sales/ShippingAddressSection";
import TrackingSection from "@/components/sales/TrackingSection";
import WebhooksList from "@/components/sales/WebhooksList";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

export default function SalesDetails() {
  const { t } = useTranslation(["sales", "common", "signatures"]);

  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { hasPermission } = usePermissions();
  const canRefund = hasPermission('order_details.refund_order');
  const canResendAccess = hasPermission('order_details.resend_customer_access');
  const canSendWhatsApp = hasPermission('order_details.send_order_whatsapp');
  const canIssueInvoice = hasPermission('order_details.issue_invoice');
  const canViewWebhooks = hasPermission('integrations.view_webhook_history');
  const canResendWebhook = hasPermission('integrations.resend_webhook');

  const [isRefundSuccessModalOpen, setIsRefundSuccessModalOpen] =
    useState(false);
  const [isRefundPixModalOpen, setIsRefundPixModalOpen] = useState(false);
  const [isRefundPendingModalOpen, setIsRefundPendingModalOpen] =
    useState(false);
  const [isRefundPayoutModalOpen, setIsRefundPayoutModalOpen] = useState(false);
  const [isRefusalHistoryOpen, setIsRefusalHistoryOpen] = useState(false);

  const breadcrumbItems = [
    { text: t("sales"), link: "/sales" },
    { text: t("saleDetails"), link: `/sales/sales-details/${id}` },
  ];

  const {
    data: sale,
    error,
    isLoading: isLoadingSale,
  } = useQuery({
    queryKey: ["getSellInfo", id],
    queryFn: async () => await SellsService.getSellInfo(id!),
  });

  // Buscar OrderRefundRequest separadamente
  const { data: refundRequest } = useQuery({
    queryKey: ["refundRequest", id],
    queryFn: async () => {
      try {
        const response = await api.get(`/v1/refund/requests/sanctum/${id}`);
        // A resposta vem como { success: true, data: {...} }
        return response.data?.data || null;
      } catch {
        return null;
      }
    },
    enabled: !!id,
  });

  // Garantia conforme prazo travado na venda (API)
  const isWarrantyExpired = (() => {
    if (typeof sale?.warranty?.is_expired === 'boolean') {
      return sale.warranty.is_expired;
    }
    if (sale?.warranty?.expires_at) {
      return new Date() > new Date(sale.warranty.expires_at);
    }
    if (!sale?.created_at) return false;
    const deadlineDays = sale?.warranty?.deadline_days ?? 7;
    const saleDate = new Date(sale.paid_at || sale.created_at);
    const now = new Date();
    const diffInDays =
      (now.getTime() - saleDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffInDays > deadlineDays;
  })();

  const { mutate: refund, isPending: isRefunding } = useMutation({
    mutationFn: async () => {
      if (!id) {
        throw new Error("ID da venda não encontrado.");
      }
      const response = await api.post(`/v1/refund/sanctum/${id}`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["getSellInfo", id] });
      queryClient.invalidateQueries({ queryKey: ["refundRequest", id] });

      if (data?.data?.requires_payout_details) {
        setIsRefundPayoutModalOpen(true);
        return;
      }

      const paymentType = sale?.payment_method?.type;

      if (paymentType === "credit_card") {
        setIsRefundSuccessModalOpen(true);
      } else if (paymentType === "pix") {
        setIsRefundPixModalOpen(true);
      }
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ["refundRequest", id] });

      if (error?.response?.data?.data?.requires_payout_details) {
        setIsRefundPayoutModalOpen(true);
        return;
      }

      const paymentType = sale?.payment_method?.type;

      if (paymentType === "credit_card") {
        setIsRefundPendingModalOpen(true);
      } else {
        setIsRefundPixModalOpen(true);
      }
    },
  });

  const { openModal } = useModalStore();
  const [selectedWebhook, setSelectedWebhook] =
    useState<WebhookDispatch | null>(null);
  const [retryingWebhookId, setRetryingWebhookId] = useState<string | null>(
    null,
  );

  const { mutate: retryWebhook } = useMutation({
    mutationFn: (webhookId: string) =>
      IntegrationsService.retrySendingWebhook(webhookId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getSellInfo", id] });
      await queryClient.invalidateQueries({
        queryKey: ["webhooks-history"],
        exact: false,
      });
      toast({ description: t("common:retry_success"), variant: "success" });
    },
    onError: (error: any) => {
      toast({
        description: error?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
    },
    onSettled: () => setRetryingWebhookId(null),
  });

  const [resendingEmailId, setResendingEmailId] = useState<string | null>(null);
  const [isSendingAccess, setIsSendingAccess] = useState(false);

  const { mutate: resendEmail } = useMutation({
    mutationFn: (emailId: string) => EmailsService.resendEmail(id!, emailId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getSellInfo", id] });
      toast({
        description: t("common:email_resend_success"),
        variant: "success",
      });
    },
    onError: (error: any) => {
      toast({
        description:
          error?.response?.data?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
    },
    onSettled: () => setResendingEmailId(null),
  });

  const { mutate: sendAccess } = useMutation({
    mutationFn: () => EmailsService.sendCustomerAccess(id!),
    onMutate: () => setIsSendingAccess(true),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getSellInfo", id] });
      toast({
        description: t("common:access_sent_success"),
        variant: "success",
      });
      useModalStore.getState().closeModal(RESEND_ACCESS_CONFIRMATION_MODAL);
    },
    onError: (error: any) => {
      toast({
        description:
          error?.response?.data?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
    },
    onSettled: () => setIsSendingAccess(false),
  });

  const handleResendEmail = (emailId: string) => {
    setResendingEmailId(emailId);
    resendEmail(emailId);
  };

  const handleResendAccess = () => {
    openModal(RESEND_ACCESS_CONFIRMATION_MODAL);
  };

  const handleConfirmResendAccess = () => {
    if (!id || sale?.status !== "paid" || !sale?.customer) return;
    sendAccess();
  };

  const handleRefund = () => {
    if (!sale || !id) return;
    refund();
  };

  const handleViewWebhookDetails = (webhook: WebhookDispatch) => {
    setSelectedWebhook(webhook);
    openModal(WEBHOOK_DETAILS_MODAL);
  };

  const handleEditCustomer = () => {
    openModal(EDIT_CUSTOMER_MODAL);
  };

  const handleRetryWebhook = (webhookId: string) => {
    setRetryingWebhookId(webhookId);
    retryWebhook(webhookId);
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = sale?.customer?.phone;
    const phonePrefix = sale?.customer?.phone_prefix || "55";
    const whatsappUrl = `https://wa.me/${phonePrefix}${phoneNumber}`;
    window.open(whatsappUrl, "_blank");
  };

  const allCountries = countries.all;

  const countryByPhonePrefix = allCountries.find((country) =>
    country.countryCallingCodes.includes(`${sale?.customer?.phone_prefix}`),
  );

  const countryByCode = sale?.customer?.country?.code
    ? allCountries.find(
      (country) => country.alpha2 === sale?.customer?.country?.code || country.alpha3 === sale?.customer?.country?.code,
    )
    : undefined;

  const countryEmoji =
    countryByCode?.emoji || countryByPhonePrefix?.emoji || "🇧🇷";
  const countryName =
    countryByCode?.name || countryByPhonePrefix?.name || "Brasil";

  const country = `${countryEmoji} ${countryName}`;

  const hasRefusalHistory =
    (sale?.not_authorized_intents?.length ?? 0) > 0;

  if (error || (!isLoadingSale && !sale)) {
    return (
      <LayoutInside items={breadcrumbItems}>
        <Button
          variant="default"
          className="flex items-center bg-transparent text-colorText hover:bg-transparent"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} />
          {t("back")}
        </Button>
        <div className="p-4 text-center">
          <XCircle className="mx-auto mb-2 text-red-600" size={48} />
          <p className="text-red-600">{t("common:data_listing_error")}</p>
        </div>
      </LayoutInside>
    );
  }

  if (isLoadingSale || !sale) {
    return (
      <LayoutInside items={breadcrumbItems}>
        <Loader fullScreen />
      </LayoutInside>
    );
  }

  const cardBrand =
    sale.paymentDetail?.credit_card?.brand ??
    sale.subscription?.payment_method?.brand ??
    undefined;
  const subscriptionPublicId =
    sale.halley_subscription_public_id ?? sale.subscription?.public_id ?? null;
  const recurringOffersWithSubscription = (sale.offers ?? []).filter(
    (offer) =>
      offer.payment_type === "recurring" && Boolean(offer.subscription_public_id),
  );
  const hasMultipleSubscriptions = recurringOffersWithSubscription.length > 1;
  const isAffiliateViewer = sale.viewer_context?.role === "affiliate";
  const canManageSale = !isAffiliateViewer;

  return (
    <LayoutInside items={breadcrumbItems}>
      <RefundSuccessModal
        open={isRefundSuccessModalOpen}
        onOpenChange={setIsRefundSuccessModalOpen}
      />
      <RefundPixModal
        open={isRefundPixModalOpen}
        onOpenChange={setIsRefundPixModalOpen}
      />
      <RefundPendingModal
        open={isRefundPendingModalOpen}
        onOpenChange={setIsRefundPendingModalOpen}
      />
      <RefundPayoutDetailsModal
        open={isRefundPayoutModalOpen}
        onOpenChange={setIsRefundPayoutModalOpen}
      />
      <motion.section initial="hidden" animate="visible" variants={fadeIn}>
          <motion.div variants={fadeInUp}>
            <Button
              variant="default"
              className="block xl:hidden bg-transparent text-colorText hover:bg-transparent"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={16} />
              {t("back")}
            </Button>
          </motion.div>
          <div className="flex gap-4 justify-between w-full max-xl:flex-col">
            <StickyContainer>
              <Sticky>
                {({
                  style,
                  isSticky,
                }: {
                  style: CSSProperties;
                  isSticky: boolean;
                }) => (
                  <motion.section
                    style={style}
                    className={`w-[328px] ${isSticky && "mt-6"} hidden xl:block`}
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    <motion.div variants={fadeInUp}>
                      <Button
                        variant="default"
                        className="bg-transparent text-colorText hover:bg-transparent"
                        onClick={() => navigate(-1)}
                      >
                        <ArrowLeft size={16} />
                        {t("back")}
                      </Button>
                    </motion.div>
                    <div>
                      <motion.div variants={fadeInUp}>
                        <Card className="lg:w-[328px] w-full p-4 md:p-6 mt-6">
                          <p className="text-muted text-sm">
                            {t("totalAmount")}
                          </p>
                          <h2 className="text-muted">
                            <strong className="text-colorText">
                              {sale?.total_formatted}
                            </strong>
                          </h2>
                          <Divider className="my-4" />
                          <article className="space-y-6 text-sm mt-4">
                            <article>
                              <p className="text-muted text-sm">
                                {t("status")}
                              </p>
                              <div className="flex items-center gap-2 flex-wrap">
                                <BadgeShared status={sale.status} />
                                {refundRequest?.status === "pending" &&
                                  sale?.status !== "refunded" && (
                                    <BadgeShared
                                      status="pending_refund"
                                      text={
                                        refundRequest?.requires_payout_details &&
                                        !refundRequest?.payout_details_submitted
                                          ? t("refundAwaitingCustomerBankDetails")
                                          : refundRequest?.requires_payout_details &&
                                              refundRequest?.payout_details_submitted
                                            ? t("refundCustomerBankDetailsReceived")
                                            : t("refundProcessing")
                                      }
                                    />
                                  )}
                                {hasRefusalHistory && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs text-muted-foreground hover:text-colorText"
                                    onClick={() => setIsRefusalHistoryOpen(true)}
                                  >
                                    <History size={14} />
                                    {t("view_attempt_history")}
                                  </Button>
                                )}
                              </div>
                            </article>

                            {sale.status === "not_authorized" && (
                              <article>
                                <p className="text-sm text-muted">
                                  {t("reason_for_refusal")}
                                </p>
                                <span>{declineReasonForDisplay(sale, t)}</span>
                              </article>
                            )}

                            {sale.payment_method && (
                              <article>
                                <p className="text-muted text-sm">
                                  {t("paymentMethod")}
                                </p>
                                <div className="flex items-center gap-2">
                                  {getIconMethodPayment(
                                    sale.payment_method.type,
                                    cardBrand,
                                  )}
                                  <span>
                                    {t("common:" + sale?.payment_method?.type)}
                                  </span>
                                </div>
                              </article>
                            )}

                            <article>
                              <p className="text-muted text-sm">
                                {t("dateAndTime")}
                              </p>
                              <span className="text-colorText text-sm">
                                {resolveSaleDisplayDate(sale)}
                              </span>
                            </article>
                          </article>
                          {canRefund && canManageSale && sale?.status === "paid" && !refundRequest && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-block mt-6">
                                    <Button
                                      variant="secondary"
                                      onClick={handleRefund}
                                      disabled={
                                        isRefunding || isWarrantyExpired
                                      }
                                    >
                                      <CornerUpLeft size={16} />
                                      {isRefunding
                                        ? t("processing")
                                        : t("refund")}
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {isWarrantyExpired && (
                                  <TooltipContent>
                                    <p>{t("warrantyExpiredMessage", { days: sale?.warranty?.deadline_days ?? 7 })}</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </Card>
                      </motion.div>
                      {/* {sale?.webhooks && sale.webhooks.length > 0 && (
                        <motion.div variants={fadeInUp}>
                          <WebhooksList
                            webhooks={sale.webhooks}
                            onViewDetails={handleViewWebhookDetails}
                            onRetry={handleRetryWebhook}
                            retryingWebhookId={retryingWebhookId}
                            className="mt-6 lg:w-[328px] w-full"
                            collapsible
                          />
                        </motion.div>
                      )} */}
                    </div>
                  </motion.section>
                )}
              </Sticky>
            </StickyContainer>

            <motion.section
              className="w-full xl:max-w-[594px]"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              <motion.section className="mt-6" variants={fadeInUp}>
                <div className="flex items-center gap-1">
                  <Details />
                  <h2 className="text-colorText font-semibold">
                    {t("saleDetails")}
                  </h2>
                </div>
                <div className="w-full mt-4 xl:hidden">
                  <p className="text-muted text-sm">{t("totalAmount")}</p>
                  <h2 className="text-muted">
                    <strong className="text-colorText">
                      {sale?.total_formatted}
                    </strong>
                  </h2>

                  {canRefund && canManageSale && sale?.status === "paid" && !refundRequest && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block mt-6 max-lg:w-full">
                            <Button
                              className="max-lg:w-full"
                              variant="secondary"
                              onClick={handleRefund}
                              disabled={isRefunding || isWarrantyExpired}
                            >
                              <CornerUpLeft size={16} />
                              {isRefunding ? t("processing") : t("refund")}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {isWarrantyExpired && (
                          <TooltipContent>
                            <p>{t("warrantyExpiredMessage", { days: sale?.warranty?.deadline_days ?? 7 })}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <section className="space-y-6 grid grid-cols-1 lg:grid-cols-2 text-sm">
                  <article className="xl:hidden mt-4">
                    <p className="text-muted text-sm">{t("status")}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <BadgeShared status={sale.status} />
                      {refundRequest?.status === "pending" &&
                        sale?.status !== "refunded" && (
                          <BadgeShared
                            status="pending_refund"
                            text={
                              refundRequest?.requires_payout_details &&
                              !refundRequest?.payout_details_submitted
                                ? t("refundAwaitingCustomerBankDetails")
                                : refundRequest?.requires_payout_details &&
                                    refundRequest?.payout_details_submitted
                                  ? t("refundCustomerBankDetailsReceived")
                                  : t("refundProcessing")
                            }
                          />
                        )}
                      {hasRefusalHistory && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-colorText"
                          onClick={() => setIsRefusalHistoryOpen(true)}
                        >
                          <History size={14} />
                          {t("view_attempt_history")}
                        </Button>
                      )}
                    </div>
                  </article>

                  {sale.status === "not_authorized" && (
                    <article className="xl:hidden">
                      <p className="text-sm text-muted">
                        {t("reason_for_refusal")}
                      </p>
                      <span>{declineReasonForDisplay(sale, t)}</span>
                    </article>
                  )}

                  {sale.payment_method && (
                    <article className="xl:hidden">
                      <p className="text-muted text-sm">{t("paymentMethod")}</p>
                      <div className="flex items-center gap-2">
                        {getIconMethodPayment(
                          sale.payment_method.type,
                          cardBrand,
                        )}
                        <span>{t("common:" + sale?.payment_method?.type)}</span>
                      </div>
                    </article>
                  )}

                  <article className="xl:hidden">
                    <p className="text-muted text-sm">{t("dateAndTime")}</p>
                    <span className="text-colorText text-sm">
                      {resolveSaleDisplayDate(sale)}
                    </span>
                  </article>

                  <article>
                    <p className="text-muted text-sm">{t("transactionId")}</p>
                    <span className="text-colorText text-sm">{sale.id}</span>
                  </article>

                  <article>
                    <p className="text-muted text-sm">{t("paymentType")}</p>
                    <span className="text-colorText text-sm">
                      {sale?.subscription?.interval_label_key
                        ? t(
                            `common:paymentFrequency.${sale.subscription.interval_label_key}`,
                          )
                        : sale?.offer_payment_type === "recurring"
                          ? t("recurring")
                          : t("single")}
                    </span>
                  </article>

                  <article>
                    <p className="text-muted text-sm">
                      {sale?.subscription ? t("billingCycle") : t("installments")}
                    </p>
                    <span className="text-colorText text-sm">
                      {sale?.subscription
                        ? (sale.subscription.current_cycle ?? "—")
                        : `${sale.paymentDetail?.installments}x`}
                    </span>
                  </article>
                  
                  {sale.payment_method?.type === "credit_card" && (
                    <article>
                      <p className="text-muted text-sm">{t("installments")}</p>
                      <span className="text-colorText text-sm">
                        {sale.paymentDetail?.installments}x
                      </span>
                    </article>
                  )}

                  {subscriptionPublicId && !hasMultipleSubscriptions && (
                    <article>
                      <p className="text-muted text-sm">{t("subscription")}</p>
                      {canManageSale ? (
                        <Link
                          to={`/signatures/subscriptions-details/${subscriptionPublicId}`}
                          className="inline-flex items-center gap-1.5 text-interactive text-sm font-medium hover:underline"
                        >
                          {t("viewSubscription")}
                          <ExternalLink size={14} />
                        </Link>
                      ) : (
                        <span className="text-colorText text-sm capitalize">
                          {sale?.subscription?.status
                            ? t(`signatures:status.${sale.subscription.status}`, {
                                defaultValue: sale.subscription.status,
                              })
                            : t("recurring")}
                        </span>
                      )}
                      {sale?.subscription?.status && canManageSale && (
                        <p className="text-muted text-xs mt-1 capitalize">
                          {t(`signatures:status.${sale.subscription.status}`, {
                            defaultValue: sale.subscription.status,
                          })}
                        </p>
                      )}
                      {sale?.subscription?.amount_formatted && (
                        <p className="text-muted text-xs mt-1">
                          {sale.subscription.amount_formatted}
                        </p>
                      )}
                      {sale?.subscription?.next_billing_at_formatted && (
                        <p className="text-muted text-xs mt-1">
                          {t("signatures:details.next_billing")}:{" "}
                          {sale.subscription.next_billing_at_formatted}
                        </p>
                      )}
                    </article>
                  )}

                  {hasMultipleSubscriptions && (
                    <article className="lg:col-span-2">
                      <p className="text-muted text-sm mb-2">
                        {t("order_subscriptions")}
                      </p>
                      <div className="space-y-2">
                        {recurringOffersWithSubscription.map((offer) => {
                          const intervalKey =
                            offer.interval_label_key ||
                            offer.recurring_interval ||
                            null;
                          return (
                            <div
                              key={
                                offer.subscription_public_id ||
                                offer.offer_id ||
                                offer.id
                              }
                              className="py-1"
                            >
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-colorText text-sm font-medium truncate">
                                    {offer.product.name}
                                  </p>
                                  <p className="text-muted text-xs mt-0.5">
                                    {[
                                      intervalKey
                                        ? t(
                                            `common:paymentFrequency.${intervalKey}`,
                                            { defaultValue: intervalKey },
                                          )
                                        : null,
                                      offer.subscription_amount_formatted,
                                      offer.subscription_current_cycle != null
                                        ? `${t("billingCycle")} ${offer.subscription_current_cycle}`
                                        : null,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </p>
                                  {offer.subscription_next_billing_at_formatted && (
                                    <p className="text-muted text-xs mt-0.5">
                                      {t("signatures:details.next_billing")}:{" "}
                                      {offer.subscription_next_billing_at_formatted}
                                    </p>
                                  )}
                                </div>
                                {canManageSale ? (
                                  <Link
                                    to={`/signatures/subscriptions-details/${offer.subscription_public_id}`}
                                    className="inline-flex items-center gap-1.5 text-interactive text-xs font-medium hover:underline shrink-0"
                                  >
                                    {t("viewSubscription")}
                                    <ExternalLink size={12} />
                                  </Link>
                                ) : offer.subscription_status ? (
                                  <span className="text-muted text-xs capitalize shrink-0">
                                    {t(`signatures:status.${offer.subscription_status}`, {
                                      defaultValue: offer.subscription_status,
                                    })}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </article>
                  )}

                  <article>
                    <p className="text-muted text-sm">{t("coupon")}</p>
                    <span className="text-muted-foreground text-sm">
                      {sale.coupon?.code
                        ? [sale.coupon.code, sale.coupon.discount_amount_formatted]
                            .filter(Boolean)
                            .join(" · ")
                        : t("notApplicable")}
                    </span>
                  </article>

                  <article>
                    <p className="text-muted text-sm">{t("shipping")}</p>
                    <span className="text-colorText text-sm">
                      {sale.shipping?.total_formatted?.trim() ||
                        t("notApplicable")}
                    </span>
                  </article>
                </section>
                {sale?.invoice_statuses && sale.invoice_statuses.length > 0 && (
                  <>
                    <Divider className="my-8" />
                    <InvoiceStatusesSection invoices={sale.invoice_statuses} />
                  </>
                )}
                {canManageSale && (
                  <div className="mt-6 gap-4 flex flex-col md:flex-row">
                    {canResendAccess && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <Button
                                variant="secondary"
                                className="w-full lg:w-[189px]"
                                disabled={
                                  sale?.status !== "paid" ||
                                  !sale?.customer ||
                                  isSendingAccess ||
                                  !sale?.can_resend_access
                                }
                                onClick={handleResendAccess}
                              >
                                {isSendingAccess ? (
                                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                                ) : (
                                  <Send size={16} />
                                )}
                                {isSendingAccess
                                  ? t("common:sending")
                                  : t("resendAccess")}
                              </Button>
                            </div>
                          </TooltipTrigger>
                          {sale?.status === "paid" &&
                            sale?.customer &&
                            !sale?.can_resend_access && (
                              <TooltipContent>
                                <p>{t("sales:limit_reached")}</p>
                              </TooltipContent>
                            )}
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {canSendWhatsApp && (
                      <Button
                        className="w-full lg:w-[171px]"
                        variant="secondary"
                        onClick={handleWhatsAppClick}
                      >
                        <WhatsIcon />
                        {t("sendWhatsApp")}
                      </Button>
                    )}
                    {canIssueInvoice &&
                      !sale.can_emit_notazz_nfe_return &&
                      !sale.can_emit_enotas_nfe_return && (
                        <EmitNoteFiscalButton
                          orderUuid={id!}
                          orderStatus={sale.status}
                          onSuccess={() =>
                            queryClient.invalidateQueries({
                              queryKey: ["getSellInfo", id],
                            })
                          }
                        />
                      )}
                    {canIssueInvoice && (
                      <EmitReturnNoteFiscalButton
                        orderUuid={id!}
                        orderStatus={sale.status}
                        canEmitNotazzReturn={!!sale.can_emit_notazz_nfe_return}
                        canEmitEnotasReturn={!!sale.can_emit_enotas_nfe_return}
                        onSuccess={() =>
                          queryClient.invalidateQueries({
                            queryKey: ["getSellInfo", id],
                          })
                        }
                      />
                    )}
                  </div>
                )}
              </motion.section>

              {sale?.currencyConversion && (
                <motion.div variants={fadeInUp}>
                  <Divider className="my-8" />

                  <div className="md:space-y-6 space-y-4">
                    <div className="flex items-center gap-1">
                      <Conversion />
                      <h2 className="text-colorText font-semibold">
                        {t("conversion")}
                      </h2>
                    </div>
                    <div className="border border-border w-full flex items-start md:items-center gap-2 p-4 rounded-lg">
                      <Info className="text-muted-foreground min-w-5" />
                      <p className="text-muted-foreground text-sm">
                        {t("paymentReceived", {
                          from: sale.currencyConversion.from_currency,
                          to: sale.currencyConversion.to_currency,
                        })}
                      </p>
                    </div>

                    <section className="grid grid-cols-1 lg:grid-cols-2 text-sm">
                      <article className="text-sm">
                        <p className="text-muted text-sm">
                          {t("paymentValue")}
                        </p>
                        <span className="text-colorText text-sm flex items-center gap-1">
                          {sale.currencyConversion.amount_formatted}{" "}
                          <ArrowRight size={12} />{" "}
                          {sale.currencyConversion.converted_amount_formatted}
                        </span>
                      </article>
                      <article>
                        <p className="text-muted text-sm">{t("grossValue")}</p>
                        <span className="text-colorText text-sm">
                          {sale.currencyConversion.converted_amount_formatted}
                        </span>
                      </article>
                    </section>
                  </div>
                </motion.div>
              )}

              {sale?.refundConversion && (
                <motion.div variants={fadeInUp}>
                  <Divider className="my-8" />

                  <div className="md:space-y-6 space-y-4">
                    <div className="flex items-center gap-1">
                      <RefundConversion />
                      <h2 className="text-colorText font-semibold">
                        {t("refundConversion")}
                      </h2>
                    </div>

                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                      <article className="text-sm">
                        <p className="text-muted text-sm">
                          {t("refundConversionValue")}
                        </p>
                        <span className="text-colorText text-sm flex items-center gap-1">
                          {sale.refundConversion.amount_formatted}{" "}
                          <ArrowRight size={12} />{" "}
                          {sale.refundConversion.converted_amount_formatted}
                        </span>
                      </article>
                      <article>
                        <p className="text-muted text-sm">
                          {t("refundConversionRefundedValue")}
                        </p>
                        <span className="text-colorText text-sm">
                          {sale.refundConversion.converted_amount_formatted}
                        </span>
                      </article>
                    </section>
                  </div>
                </motion.div>
              )}

              <motion.div variants={fadeInUp}>
                <Divider className="my-8" />

                <div className="md:space-y-6 space-y-4">
                  <div className="flex items-center gap-1">
                    <TagsCircle />
                    <h2 className="text-colorText font-semibold">
                      {t("products")}
                    </h2>
                  </div>

                  <section className="flex gap-4 w-full">
                    <SaleSlider sale={sale} />
                  </section>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <Divider className="my-8" />

                <div className="md:space-y-6 space-y-4">
                  <div className="flex items-center gap-1">
                    <ClientCircle />
                    <h2 className="text-colorText font-semibold">
                      {t("client")}
                    </h2>
                  </div>

                  <section className="grid gap-6 grid-cols-1 lg:grid-cols-2 text-sm">
                    <article className="text-sm">
                      <p className="text-muted text-sm">{t("name")}</p>
                      <span className="text-colorText text-sm">
                        {sale.customer?.name}
                      </span>
                    </article>
                    <article>
                      <div className="flex items-center gap-2">
                        <p className="text-muted text-sm">{t("email")}</p>
                        {canManageSale && sale?.customer?.can_update_email && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-muted-foreground hover:text-colorText"
                            onClick={handleEditCustomer}
                          >
                            <Edit size={12} />
                          </Button>
                        )}
                      </div>
                      <span className="text-colorText text-sm">
                        {sale.customer?.email}
                      </span>
                    </article>
                    <article>
                      <p className="text-muted text-sm">{t("phone")}</p>
                      <span className="text-colorText text-sm">
                        {sale?.customer?.phone_prefix || "55"}
                        {sale.customer.phone}
                      </span>
                    </article>
                    <article>
                      <p className="text-muted text-sm">{t("cpf")}</p>
                      <span className="text-colorText text-sm">
                        {sale.customer?.document?.document_value ?? "----"}
                      </span>
                    </article>
                    <article>
                      <p className="text-muted text-sm">{t("country")}</p>
                      <span className="text-colorText text-sm flex items-center gap-1">
                        <p>{country}</p>
                      </span>
                    </article>
                    <article>
                      <p className="text-muted text-sm">{t("address")}</p>
                      <span className="text-colorText text-sm block">
                        {sale?.address ? (
                          <>
                            {sale?.address?.street}, {sale?.address?.number}
                            {sale?.address?.complement &&
                              ` - ${sale?.address?.complement}`}
                            <br />
                            {sale?.address?.district}, {sale?.address?.city} -{" "}
                            {sale?.address?.state}
                            <br />
                            {sale?.address?.postal_code}
                          </>
                        ) : (
                          "----"
                        )}
                      </span>
                    </article>
                  </section>
                </div>
              </motion.div>

              {(sale.delivery_trackings?.length || sale.delivery_tracking) && (
                <motion.div variants={fadeInUp}>
                  <Divider className="my-8" />
                  <TrackingSection
                    tracking={sale.delivery_tracking}
                    trackings={sale.delivery_trackings}
                  />
                </motion.div>
              )}

              {sale.delivery_timeline && sale.delivery_timeline.length > 0 && (
                <motion.div variants={fadeInUp}>
                  <Divider className="my-8" />
                  <DeliveryTimeline
                    timeline={sale.delivery_timeline}
                    collapsible
                  />
                </motion.div>
              )}

              {sale.shipping_address && (
                <motion.div variants={fadeInUp}>
                  <Divider className="my-8" />
                  <ShippingAddressSection address={sale.shipping_address} />
                </motion.div>
              )}


              <motion.div variants={fadeInUp}>
                <Divider className="my-8" />
                <SalesCommissionsSection sale={sale} />
              </motion.div>

              <motion.div variants={fadeInUp}>
                <Divider className="my-8" />

                <div className="md:space-y-6 space-y-4">
                  <div className="flex items-center gap-1">
                    <LinkCircle />
                    <h2 className="text-colorText font-semibold">
                      {t("utms")}
                    </h2>
                  </div>

                  <section className="grid gap-6 grid-cols-1 lg:grid-cols-2 text-sm">
                    <article className="text-sm">
                      <p className="text-muted text-sm">{t("src")}</p>
                      <span
                        className={
                          sale.tracking?.src
                            ? "text-colorText break-all text-sm"
                            : "text-muted-foreground break-all text-sm"
                        }
                      >
                        {sale.tracking?.src ?? t("undefined")}
                      </span>
                    </article>
                    <article>
                      <p className="text-muted text-sm">{t("utmMedium")}</p>
                      <span
                        className={
                          sale.tracking?.utm_medium
                            ? "text-colorText break-all text-sm"
                            : "text-muted-foreground break-all text-sm"
                        }
                      >
                        {sale.tracking?.utm_medium ?? t("undefined")}
                      </span>
                    </article>
                    <article>
                      <p className="text-muted text-sm">{t("utmTerm")}</p>
                      <span
                        className={
                          sale.tracking?.utm_term
                            ? "text-colorText break-all text-sm"
                            : "text-muted-foreground break-all text-sm"
                        }
                      >
                        {sale.tracking?.utm_term ?? t("undefined")}
                      </span>
                    </article>
                    <article>
                      <p className="text-muted text-sm">{t("utmSource")}</p>
                      <span
                        className={
                          sale.tracking?.utm_source
                            ? "text-colorText break-all text-sm"
                            : "text-muted-foreground break-all text-sm"
                        }
                      >
                        {sale.tracking?.utm_source ?? t("undefined")}
                      </span>
                    </article>
                    <article>
                      <p className="text-muted text-sm">{t("utmCampaign")}</p>
                      <span
                        className={
                          sale.tracking?.utm_campaign
                            ? "text-colorText break-all text-sm"
                            : "text-muted-foreground break-all text-sm"
                        }
                      >
                        {sale.tracking?.utm_campaign ?? t("undefined")}
                      </span>
                    </article>
                    <article>
                      <p className="text-muted text-sm">{t("utmContent")}</p>
                      <span
                        className={
                          sale.tracking?.utm_content
                            ? "text-colorText break-all text-sm"
                            : "text-muted-foreground break-all text-sm"
                        }
                        style={{ wordBreak: "break-all" }}
                      >
                        {sale.tracking?.utm_content ?? t("undefined")}
                      </span>
                    </article>
                  </section>
                </div>
              </motion.div>

              {canViewWebhooks && sale?.webhooks && sale.webhooks.length > 0 && (
                <motion.div variants={fadeInUp}>
                  <Divider className="my-8" />
                  <WebhooksList
                    webhooks={sale.webhooks}
                    onViewDetails={handleViewWebhookDetails}
                    onRetry={canManageSale && canResendWebhook ? handleRetryWebhook : undefined}
                    retryingWebhookId={retryingWebhookId}
                    className="w-full"
                    collapsible
                  />
                </motion.div>
              )}

              {sale?.customer_emails && sale?.customer_emails?.length > 0 && (
                <motion.div variants={fadeInUp}>
                  <Divider className="my-8" />
                  <EmailsList
                    emails={sale.customer_emails}
                    onResend={canManageSale ? handleResendEmail : undefined}
                    resendingEmailId={resendingEmailId}
                    className="w-full mb-24"
                    collapsible
                  />
                </motion.div>
              )}
            </motion.section>
          </div>
        </motion.section>

      <WebhookDetailsModal
        webhook={selectedWebhook}
        onRetrySuccess={() => setSelectedWebhook(null)}
      />
      <ResendAccessConfirmationModal
        onConfirm={handleConfirmResendAccess}
        isSending={isSendingAccess}
        customerEmail={sale.customer?.email}
        canUpdateEmail={sale.customer?.can_update_email}
        onEditEmail={() => {
          openModal(EDIT_CUSTOMER_MODAL);
          useModalStore.getState().closeModal(RESEND_ACCESS_CONFIRMATION_MODAL);
        }}
      />
      <EditCustomerModal customer={sale.customer} />
      {hasRefusalHistory && sale.not_authorized_intents && (
        <RefusalHistoryModal
          open={isRefusalHistoryOpen}
          onOpenChange={setIsRefusalHistoryOpen}
          intents={sale.not_authorized_intents}
        />
      )}
    </LayoutInside>
  );
}
